import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * App-contributed TopBar slots.
 *
 * The TopBar is shell-owned chrome; remotes cannot render it. But the source
 * app (clario-frontend) composes projects-domain widgets — MeetingDropdown,
 * TimerWidget, quick actions, the real NotificationPopover — directly into
 * its header. To replicate that without breaking the host/remote boundary,
 * the TopBar renders empty portal *targets* and the active app fills them
 * with <TopBarPortal> from inside its own provider tree, so the widgets keep
 * their Redux store, contexts, and react-query client.
 *
 * @clarix/layout is an MF singleton, so this module-level registry is shared
 * between the shell bundle and every remote.
 */

export type TopBarSlotId =
  | 'meeting-chip'
  | 'timer'
  | 'notifications'
  | 'quick-actions'
  /**
   * After the user menu — the last thing on the bar.
   *
   * Separate from `quick-actions` because that slot sits BEFORE the avatar and
   * holds the actions of the app you are in (new task, schedule meeting). A
   * trailing affordance that belongs to the platform rather than the app — Spark
   * is the one today — goes here, so it lands in the same pixel in every app
   * regardless of how many quick actions that app contributes.
   */
  | 'trailing';

const SLOT_DOM_ID: Record<TopBarSlotId, string> = {
  'meeting-chip': 'clarix-topbar-slot-meeting-chip',
  timer: 'clarix-topbar-slot-timer',
  notifications: 'clarix-topbar-slot-notifications',
  'quick-actions': 'clarix-topbar-slot-quick-actions',
  trailing: 'clarix-topbar-slot-trailing',
};

// ---- filled-slot registry (lets the TopBar hide fallbacks, e.g. the
// empty-state notification bell, while an app provides the real widget) ----

const filled = new Map<TopBarSlotId, number>();
const listeners = new Set<() => void>();
// Bumped on every register/unregister; getSnapshot must return a new value
// for useSyncExternalStore to notify subscribers.
let version = 0;

function register(slot: TopBarSlotId): () => void {
  filled.set(slot, (filled.get(slot) ?? 0) + 1);
  version += 1;
  listeners.forEach((l) => l());
  return () => {
    const count = (filled.get(slot) ?? 1) - 1;
    if (count <= 0) filled.delete(slot);
    else filled.set(slot, count);
    version += 1;
    listeners.forEach((l) => l());
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** True while some app has a <TopBarPortal> mounted for this slot. */
export function useTopBarSlotFilled(slot: TopBarSlotId): boolean {
  useSyncExternalStore(subscribe, () => version);
  return filled.has(slot);
}

/** Portal target rendered by the TopBar. `empty:hidden` keeps an unfilled
 *  target from adding a flex-gap step to the header row. */
export function TopBarSlotTarget({ slot }: { slot: TopBarSlotId }) {
  return <div id={SLOT_DOM_ID[slot]} className="flex shrink-0 items-center gap-2.5 empty:hidden" />;
}

interface TopBarPortalProps {
  slot: TopBarSlotId;
  children: ReactNode;
}

/**
 * Renders `children` into the TopBar slot target while keeping them in the
 * caller's React tree (contexts, Redux provider, query client all apply).
 * Renders nothing when no TopBar is mounted (e.g. raw standalone mode).
 */
export function TopBarPortal({ slot, children }: TopBarPortalProps) {
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.getElementById(SLOT_DOM_ID[slot]);
    setTarget(el);
    if (!el) return;
    return register(slot);
  }, [slot]);

  return target ? createPortal(children, target) : null;
}
